/**
 * MAHI Robot — the little flying companion.
 *
 * Pure SVG + framer-motion. Soft white orb with a mood-driven identity:
 * glow color, ring, float rhythm, eyes and mouth all shift with `mood`, plus
 * an idle blink so it reads as "alive" even when nothing is happening.
 * Speech bubble is optional (pass `say="..."`).
 *
 * Moods: 'happy' (default/idle) | 'thinking' (busy/loading) |
 *        'celebrating' (success) | 'alert' (error) | 'sleep' (waiting screens)
 *
 * Pair with the `useMahiMood()` hook exported below to flash a mood for a
 * request (thinking while in-flight, celebrating/alert on the result, then
 * back to happy) without every page hand-rolling setTimeout logic.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const MOODS = {
  happy:       { glow: 'rgba(34,211,238,0.25)',  glow2: 'rgba(34,211,238,0.12)',  rim: ['#fde68a', '#facc15'], floatY: 8,  floatDuration: 4,   pulse: 1.015, pulseDuration: 3.2 },
  thinking:    { glow: 'rgba(250,204,21,0.32)',  glow2: 'rgba(250,204,21,0.14)',  rim: ['#a5f3fc', '#22d3ee'], floatY: 5,  floatDuration: 2.2, pulse: 1.02,  pulseDuration: 1.1, spin: true },
  celebrating: { glow: 'rgba(250,204,21,0.48)',  glow2: 'rgba(34,211,238,0.24)',  rim: ['#fde68a', '#f59e0b'], floatY: 11, floatDuration: 1,   pulse: 1.06,  pulseDuration: 0.6, sparkle: true },
  alert:       { glow: 'rgba(248,113,113,0.35)', glow2: 'rgba(248,113,113,0.15)', rim: ['#fca5a5', '#ef4444'], floatY: 3,  floatDuration: 3.2, pulse: 1,     pulseDuration: 1,   shake: true },
  sleep:       { glow: 'rgba(148,163,184,0.20)', glow2: 'rgba(148,163,184,0.10)', rim: ['#e2e8f0', '#cbd5e1'], floatY: 3,  floatDuration: 6,   pulse: 1,     pulseDuration: 4 },
};

const Eyes = ({ mood, blink }) => {
  const scaleY = blink ? 0.08 : 1;
  const common = { animate: { scaleY }, transition: { duration: 0.09 }, style: { transformOrigin: '60px 53px' } };

  if (mood === 'sleep') {
    return (
      <>
        <path d="M38 52 q6 -6 12 0" stroke="#94a3b8" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M70 52 q6 -6 12 0" stroke="#94a3b8" strokeWidth="3" fill="none" strokeLinecap="round" />
      </>
    );
  }
  if (mood === 'celebrating') {
    return (
      <>
        <path d="M37 55 q7 -9 14 0" stroke="#f59e0b" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        <path d="M69 55 q7 -9 14 0" stroke="#f59e0b" strokeWidth="3.5" fill="none" strokeLinecap="round" />
      </>
    );
  }
  if (mood === 'alert') {
    return (
      <motion.g {...common}>
        <circle cx="44" cy="53" r="6.5" fill="#ef4444" />
        <circle cx="76" cy="53" r="6.5" fill="#ef4444" />
        <circle cx="42" cy="51" r="1.6" fill="#ffffff" />
        <circle cx="74" cy="51" r="1.6" fill="#ffffff" />
      </motion.g>
    );
  }
  if (mood === 'thinking') {
    return (
      <motion.g {...common}>
        <circle cx="45" cy="50" r="4.5" fill="#0e7490" />
        <circle cx="77" cy="50" r="4.5" fill="#0e7490" />
      </motion.g>
    );
  }
  // happy (default)
  return (
    <motion.g {...common}>
      <circle cx="44" cy="54" r="5" fill="#06b6d4" />
      <circle cx="76" cy="54" r="5" fill="#06b6d4" />
      <circle cx="42.5" cy="52.5" r="1.4" fill="#ffffff" />
      <circle cx="74.5" cy="52.5" r="1.4" fill="#ffffff" />
    </motion.g>
  );
};

const Mouth = ({ mood }) => {
  if (mood === 'alert') return <path d="M48 78 h24" stroke="#cbd5e1" strokeWidth="2" fill="none" strokeLinecap="round" />;
  if (mood === 'celebrating') return <path d="M46 75 q14 12 28 0" stroke="#f59e0b" strokeWidth="2.5" fill="none" strokeLinecap="round" />;
  return <path d="M48 76 q12 8 24 0" stroke="#cbd5e1" strokeWidth="2" fill="none" strokeLinecap="round" />;
};

const Sparkles = () => (
  <>
    {[0, 1, 2, 3].map((i) => {
      const angle = (i / 4) * 2 * Math.PI;
      const x = 50 + Math.cos(angle) * 46;
      const y = 50 + Math.sin(angle) * 46;
      return (
        <motion.div
          key={i}
          className="absolute w-1.5 h-1.5 rounded-full bg-amber-300"
          style={{ left: `${x}%`, top: `${y}%` }}
          animate={{ opacity: [0, 1, 0], scale: [0.4, 1.2, 0.4] }}
          transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.22, ease: 'easeInOut' }}
        />
      );
    })}
  </>
);

const MahiRobot = ({ size = 120, say, mood = 'happy' }) => {
  const cfg = MOODS[mood] || MOODS.happy;
  const [blink, setBlink] = useState(false);

  // Idle blink — skipped for alert (stays wide-eyed) and sleep (already closed).
  useEffect(() => {
    if (mood === 'alert' || mood === 'sleep') return undefined;
    let timeout;
    const scheduleBlink = () => {
      timeout = setTimeout(() => {
        setBlink(true);
        setTimeout(() => setBlink(false), 120);
        scheduleBlink();
      }, 2500 + Math.random() * 3000);
    };
    scheduleBlink();
    return () => clearTimeout(timeout);
  }, [mood]);

  const float = {
    animate: {
      y: [0, -cfg.floatY, 0],
      scale: [1, cfg.pulse, 1],
      ...(cfg.shake ? { x: [0, -3, 3, -3, 0] } : {}),
    },
    transition: { duration: cfg.floatDuration, repeat: Infinity, ease: 'easeInOut' },
  };

  return (
    <div className="relative inline-flex flex-col items-center" data-testid="mahi-robot" data-mood={mood}>
      <motion.div {...float} style={{ width: size, height: size }} className="relative">
        {/* Mood-driven outer glow */}
        <motion.div
          className="absolute inset-0 rounded-full"
          animate={{ boxShadow: [
            `0 0 50px 6px ${cfg.glow}, 0 0 100px 14px ${cfg.glow2}`,
            `0 0 70px 10px ${cfg.glow}, 0 0 130px 18px ${cfg.glow2}`,
            `0 0 50px 6px ${cfg.glow}, 0 0 100px 14px ${cfg.glow2}`,
          ] }}
          transition={{ duration: cfg.pulseDuration, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Particle wings */}
        <motion.div
          className="absolute -left-6 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-300/60"
          animate={{ opacity: [0.2, 0.9, 0.2], scale: [0.7, 1.1, 0.7] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -right-6 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-300/60"
          animate={{ opacity: [0.2, 0.9, 0.2], scale: [0.7, 1.1, 0.7] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut', delay: 1.1 }}
        />
        {cfg.sparkle && <Sparkles />}

        {/* The orb itself */}
        <svg viewBox="0 0 120 120" className="relative w-full h-full">
          <defs>
            <radialGradient id="orbBody" cx="38%" cy="32%" r="70%">
              <stop offset="0%"  stopColor="#ffffff" />
              <stop offset="55%" stopColor="#f8fafc" />
              <stop offset="100%" stopColor="#e2e8f0" />
            </radialGradient>
            <linearGradient id="orbRim" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor={cfg.rim[0]} stopOpacity="0.6" />
              <stop offset="100%" stopColor={cfg.rim[1]} stopOpacity="0.3" />
            </linearGradient>
          </defs>
          {/* rim — spins as a "processing" ring while thinking */}
          {cfg.spin ? (
            <motion.circle
              cx="60" cy="60" r="48" fill="none" stroke="url(#orbRim)" strokeWidth="2.5"
              strokeDasharray="70 210" strokeLinecap="round"
              animate={{ rotate: 360 }} transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
              style={{ transformOrigin: '60px 60px' }}
            />
          ) : (
            <circle cx="60" cy="60" r="48" fill="none" stroke="url(#orbRim)" strokeWidth="1.5" />
          )}
          {/* body */}
          <circle cx="60" cy="60" r="46" fill="url(#orbBody)" stroke="#e2e8f0" strokeWidth="1" />
          {/* glossy highlight */}
          <ellipse cx="46" cy="40" rx="14" ry="8" fill="#ffffff" opacity="0.7" />
          <Mouth mood={mood} />
          <Eyes mood={mood} blink={blink} />
        </svg>
      </motion.div>

      {say && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="mt-3 max-w-[260px] px-4 py-2 rounded-2xl bg-white shadow-[0_6px_24px_-8px_rgba(15,23,42,0.18)] ring-1 ring-slate-100 text-sm text-slate-700 text-center"
        >
          {say}
        </motion.div>
      )}
    </div>
  );
};

export default MahiRobot;

/**
 * useMahiMood — flash a mood for the duration of an async action, then
 * settle back to 'happy'. Saves every page from re-implementing the same
 * setMood('thinking') / setTimeout(() => setMood('happy')) dance.
 *
 *   const { mood, withMood } = useMahiMood();
 *   await withMood(() => mahi.post(...), { success: 'celebrating', error: 'alert' });
 */
export const useMahiMood = (initial = 'happy') => {
  const [mood, setMood] = useState(initial);
  const settleTimer = useRef(null);

  const flash = (next, holdMs = 1400) => {
    clearTimeout(settleTimer.current);
    setMood(next);
    settleTimer.current = setTimeout(() => setMood('happy'), holdMs);
  };

  const withMood = async (fn, { success = 'celebrating', error = 'alert', holdMs = 1400 } = {}) => {
    clearTimeout(settleTimer.current);
    setMood('thinking');
    try {
      const result = await fn();
      flash(success, holdMs);
      return result;
    } catch (e) {
      flash(error, holdMs);
      throw e;
    }
  };

  useEffect(() => () => clearTimeout(settleTimer.current), []);

  return { mood, setMood, flash, withMood };
};
